import React, { useCallback, useEffect, useMemo, useState } from "react"
import { DatePicker, Spin, Tooltip, message } from "antd"
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { Analytics } from "../../Apis/analyticsApi"
import DropdownFilter from "../../components/Common/DropdownFilter"
import { translations } from "../../utils/Translation/staticTranslation"
import PlanUsageTabs from "./PlanUsageTabs"
import "../../components/PlansPricing/PlanUsage.css"

const { RangePicker } = DatePicker

// Same options, order and labels as the Plan Usage tab's date filter — the two
// tabs share a page, so switching between them must not change the vocabulary.
// "Till Date" is the backend's `alltime`.
const RANGE_OPTIONS = {
  en: [
    { label: "Till Date", value: "tilldate" },
    { label: "Last 7 days", value: "last7days" },
    { label: "Last 30 days", value: "last30days" },
    { label: "Last 90 days", value: "last90days" },
    { label: "Custom Range", value: "customdate" },
  ],
  ar: [
    { label: "حتى التاريخ", value: "tilldate" },
    { label: "آخر ٧ أيام", value: "last7days" },
    { label: "آخر ٣٠ يومًا", value: "last30days" },
    { label: "آخر ٩٠ يومًا", value: "last90days" },
    { label: "نطاق مخصص", value: "customdate" },
  ],
}

// Reused from the Dashboard's Feature Usage chart so a tool carries the same
// glyph on both pages. Anything unmapped falls back to a lettered badge rather
// than a broken <img>.
const TOOL_ICONS = {
  home_redesign: "/dashboard/room-redesign-icon.svg",
  object_removal: "/dashboard/object-removal-icon.svg",
  sketch_to_render: "/dashboard/quick-edits.svg",
  text_to_image: "/dashboard/credits-icon.svg",
  furniture_finder: "/dashboard/sofa-icon.svg",
  furniture_removal: "/dashboard/virtua_unstaging.svg",
  furniture_replace: "/dashboard/replace-furniture.svg",
  rearrange_furniture: "/dashboard/rearrange-furniture.svg",
  enhance_image: "/dashboard/image-upload.svg",
  expert_design: "/dashboard/event-booth-icon.svg",
  reference_style: "/dashboard/reference-style-icon.svg",
  wall_design: "/dashboard/wall-design-icon.svg",
  floor_design: "/dashboard/floor-design-icon.svg",
  floor_generation: "/dashboard/floor-generation-icon.svg",
}

// One accent per rank, cycled — the same five the Plan Usage tab uses for plans.
const TOOL_ACCENTS = ["#C9A227", "#4FA971", "#8B5CF6", "#B98A5A", "#3B82F6"]

const accentFor = (index) => TOOL_ACCENTS[index % TOOL_ACCENTS.length]

const formatNumber = (value) => {
  if (value == null) return "—"
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(Number(value)) ? 0 : 1,
    maximumFractionDigits: 1,
  })
}

const formatPercent = (value) => (value == null ? "—" : `${value}%`)

const SummaryCard = ({ icon, label, value, caption, tint }) => (
  <div className="plan-usage-summary-card">
    <div className="plan-usage-summary-icon" style={{ background: tint }}>
      {icon}
    </div>
    <div className="plan-usage-summary-body">
      <span className="plan-usage-summary-label">{label}</span>
      <span className="plan-usage-summary-value">{value}</span>
      <span className="plan-usage-summary-caption">{caption}</span>
    </div>
  </div>
)

/** Icon for a tool, or its initial when we have no glyph for that key yet. */
const ToolGlyph = ({ toolKey, label, accent }) => {
  const src = TOOL_ICONS[toolKey]
  if (!src) {
    return (
      <span
        className="tool-usage-glyph tool-usage-glyph-letter"
        style={{ background: `${accent}1F`, color: accent }}
      >
        {String(label || "?").trim()[0]?.toUpperCase() || "?"}
      </span>
    )
  }
  return (
    <span className="tool-usage-glyph">
      <img src={src} alt="" aria-hidden="true" />
    </span>
  )
}

/**
 * Horizontal meter. `scale` is the value that fills the track: the leader for
 * the relative-magnitude column, 100 for the share column.
 */
const Meter = ({ value, scale, color }) => (
  <div className="tool-usage-meter-track">
    <div
      className="tool-usage-meter-fill"
      style={{
        width: `${Math.min(Math.max(scale ? (value / scale) * 100 : 0, 0), 100)}%`,
        background: color,
      }}
    />
  </div>
)

const SubscriberToolUsage = () => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.toolUsagePage || {}
  const isArabic = lang === "ar"

  const options = RANGE_OPTIONS[isArabic ? "ar" : "en"]

  // Matches the Plan Usage tab's default so switching tabs keeps the window.
  const [filterType, setFilterType] = useState("tilldate")
  const [range, setRange] = useState(null)
  const [segment, setSegment] = useState("all")
  const [plan, setPlan] = useState("all")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const requestParams = useMemo(() => {
    const base = { subscribers: segment, plan }
    if (filterType !== "customdate") {
      return {
        ...base,
        filterType: filterType === "tilldate" ? "alltime" : filterType,
      }
    }
    return {
      ...base,
      filterType: "customdate",
      startDate: range?.[0]?.format("YYYY-MM-DD"),
      endDate: range?.[1]?.format("YYYY-MM-DD"),
    }
  }, [filterType, range, segment, plan])

  // Custom Range shows the picker first and only fetches once both ends are
  // chosen, so selecting it does not fire a request with null dates.
  const paramsReady =
    requestParams.filterType !== "customdate" ||
    Boolean(requestParams.startDate && requestParams.endDate)

  const fetchStats = useCallback(async () => {
    if (!paramsReady) return
    setLoading(true)
    try {
      const response = await Analytics.getSubscriberToolUsage(requestParams)
      setData(response?.data || null)
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          t.error_fetching ||
          "Could not load tool usage statistics."
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [requestParams, paramsReady, t.error_fetching])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleExport = async () => {
    setExporting(true)
    try {
      const response = await Analytics.exportSubscriberToolUsage(requestParams)
      const blob = response?.data
      if (!(blob instanceof Blob)) {
        message.error(t.error_export || "Could not export the CSV.")
        return
      }
      const disposition = response?.headers?.["content-disposition"]
      const match = disposition?.match(
        /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
      )
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = match?.[1]?.trim() || "subscriber-tool-usage.csv"
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      message.error(t.error_export || "Could not export the CSV.")
    } finally {
      setExporting(false)
    }
  }

  const summary = data?.summary
  const tools = data?.tools || []
  // Every bar in the relative column is sized against the leader, so the top row
  // is always a full track and the rest read as a share of it.
  const leader = tools[0]?.generations || 0

  const segmentOptions = [
    { label: t.segment_all || "All", value: "all" },
    { label: t.segment_paid || "Paid subscribers", value: "paid" },
    { label: t.segment_trial || "Free / trial users", value: "trial" },
  ]

  const planOptions = [
    { label: t.all_plans || "All Plans", value: "all" },
    ...(data?.filters?.plans || []).map((item) => ({
      // Monthly and yearly tiers share a name, so the billing period has to be
      // on the option or two entries look like duplicates.
      label: `${
        isArabic ? item.plan_name_arabic || item.plan_name : item.plan_name
      }${item.duration_type ? ` · ${item.duration_type}` : ""}`,
      value: String(item.plan_id),
    })),
  ]

  // Choosing a plan implies the paid population — the backend says so too, and
  // leaving the segment dropdown showing "All" would misdescribe the table.
  const planLocked = plan !== "all"

  return (
    <div className="plan-usage-page">
      <div className="plan-usage-header">
        <div>
          <h1 className="plan-usage-title">
            {t.title || "Subscriber Generation"}
          </h1>
          <p className="plan-usage-subtitle">
            {t.subtitle || "How subscribers are using Zory's generative tools"}
          </p>
        </div>
        <div className="plan-usage-actions">
          <PlanUsageTabs />
          {filterType === "customdate" && (
            <RangePicker
              value={range}
              onChange={(value) => setRange(value || null)}
              allowClear={false}
              // No future dates: there is no usage to report past today.
              disabledDate={(current) => current && current > dayjs().endOf("day")}
              format="MMM D, YYYY"
              className="plan-usage-rangepicker"
            />
          )}
          <DropdownFilter
            width="205px"
            label={options[0].label}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={options}
            selectedCategory={filterType}
            onSelect={setFilterType}
          />
          <button
            type="button"
            className="plan-usage-export"
            onClick={handleExport}
            disabled={exporting || loading || !tools.length}
          >
            <DownloadOutlined />
            <span>{t.export || "Export"}</span>
          </button>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className="plan-usage-summary-grid tool-usage-summary-grid">
          <SummaryCard
            tint="#EFEAFB"
            icon={<span className="plan-usage-emoji">🖼️</span>}
            label={t.total_generations || "Total Generations"}
            value={formatNumber(summary?.total_generations ?? 0)}
            caption={t.across_all_tools || "Across all tools"}
          />
          <SummaryCard
            tint="#E7F5EC"
            icon={<span className="plan-usage-emoji">🏆</span>}
            label={t.most_used_tool || "Most Used Tool"}
            value={
              <span className="tool-usage-top-name">
                {(isArabic
                  ? summary?.top_tool?.label_arabic
                  : summary?.top_tool?.label) || "—"}
              </span>
            }
            caption={
              summary?.top_tool
                ? `${formatNumber(summary.top_tool.generations)} ${
                    t.generations_lower || "generations"
                  }`
                : t.no_data_caption || "No generations yet"
            }
          />
          <SummaryCard
            tint="#E8F1FD"
            icon={<span className="plan-usage-emoji">📊</span>}
            label={t.share_of_top_tool || "Share of Top Tool"}
            value={formatPercent(summary?.top_tool_percent)}
            caption={
              (isArabic
                ? summary?.top_tool?.label_arabic
                : summary?.top_tool?.label) ||
              t.of_all_generations ||
              "Of all generations"
            }
          />
        </div>

        <div className="tool-usage-filters">
          <span className="tool-usage-filter-label">
            {t.subscribers || "Subscribers"}
          </span>
          <DropdownFilter
            width="200px"
            label={segmentOptions[0].label}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={segmentOptions}
            selectedCategory={planLocked ? "paid" : segment}
            onSelect={(value) => {
              if (planLocked) return
              setSegment(value)
            }}
          />
          <span className="tool-usage-filter-label">{t.plan || "Plan"}</span>
          <DropdownFilter
            width="230px"
            label={planOptions[0].label}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={planOptions}
            selectedCategory={plan}
            onSelect={setPlan}
          />
          {planLocked && (
            <span className="tool-usage-filter-note">
              {t.plan_implies_paid ||
                "A plan filter covers that plan's paid subscribers."}
            </span>
          )}
        </div>

        <section className="plan-usage-panel">
          <div className="tool-usage-panel-head">
            <div>
              <h2 className="plan-usage-panel-title">
                {t.generations_by_tool || "Generations by Tool"}
              </h2>
              <p className="plan-usage-panel-subtitle">
                {t.panel_subtitle ||
                  "Percentage shows each tool's share of total generations."}
              </p>
            </div>
            <span className="tool-usage-total">
              {t.total_generations_inline || "Total Generations"}:{" "}
              {formatNumber(summary?.total_generations ?? 0)}
            </span>
          </div>

          <div className="plan-usage-table-scroll">
            <table className="plan-usage-table tool-usage-table">
              <thead>
                <tr>
                  <th className="tool-usage-th-rank">#</th>
                  <th className="plan-usage-th-plan">{t.tool || "Tool"}</th>
                  <th className="tool-usage-th-meter">
                    <span className="plan-usage-th-with-info">
                      {t.relative_use || "Relative Use"}
                      <Tooltip
                        title={
                          t.relative_tooltip ||
                          "Each bar is sized against the most-used tool, so the leader fills the track."
                        }
                      >
                        <InfoCircleOutlined className="plan-usage-info" />
                      </Tooltip>
                    </span>
                  </th>
                  <th>{t.generations || "Generations"}</th>
                  <th>{t.percent_of_total || "% of Total"}</th>
                  <th className="tool-usage-th-meter">
                    <span className="plan-usage-th-with-info">
                      {t.share_of_total || "Share of Total Generations"}
                      <Tooltip
                        title={
                          t.share_tooltip ||
                          "The same percentage drawn against a full 100% track."
                        }
                      >
                        <InfoCircleOutlined className="plan-usage-info" />
                      </Tooltip>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tools.map((row, index) => {
                  const accent = accentFor(index)
                  const label = isArabic ? row.label_arabic : row.label
                  return (
                    <tr key={row.tool}>
                      <td className="tool-usage-td-rank">
                        <span className="tool-usage-rank">{row.rank}</span>
                      </td>
                      <td className="plan-usage-td-plan">
                        <ToolGlyph
                          toolKey={row.tool}
                          label={label}
                          accent={accent}
                        />
                        <span className="plan-usage-plan-name">{label}</span>
                      </td>
                      <td>
                        <Meter
                          value={row.generations}
                          scale={leader}
                          color={accent}
                        />
                      </td>
                      <td>
                        <span className="plan-usage-metric">
                          {formatNumber(row.generations)}
                        </span>
                      </td>
                      <td>{formatPercent(row.percent_of_total)}</td>
                      <td>
                        <div className="tool-usage-share-cell">
                          <span className="tool-usage-share-value">
                            {formatPercent(row.percent_of_total)}
                          </span>
                          <Meter
                            value={row.percent_of_total}
                            scale={100}
                            color={accent}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {!loading && !tools.length && (
                  <tr>
                    <td colSpan={6} className="plan-usage-empty">
                      {t.no_data || "No generations for this period."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="tool-usage-note">
          <InfoCircleOutlined />
          <span>
            {t.footnote ||
              "Metrics count successful generations across the selected subscribers."}
          </span>
        </div>
      </Spin>
    </div>
  )
}

export default SubscriberToolUsage
